import Sun32 from '@carbon/icons-react/es/sun/32';
import Sunrise32 from '@carbon/icons-react/es/sunrise/32';
import Sunset32 from '@carbon/icons-react/es/sunset/32';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { Page } from 'components/molecules/page/page';
import { useCustomerPreview } from 'hooks/use-customer-preview';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { fullName } from 'utils/full-name';

/**
 * { 'month': { 'day': 'translation_code' } }
 */
const AVAILABLE_HOLIDAYS = {
  12: {
    25: 'christmas',
  },
  1: {
    1: 'new_year',
  },
};

const getHolidayCode = (day: number, month: number) => {
  return get(AVAILABLE_HOLIDAYS, `${month}.${day}`);
};

const useGreeting = (time = moment()): [string, JSX.Element] => {
  const currentHour = Number(time.format('HH'));
  const currentMonth = Number(time.format('MM'));
  const currentDay = Number(time.format('DD'));
  const holidayCode = getHolidayCode(currentDay, currentMonth);
  const intl = useIntl();
  const afternoon = 12;
  const evening = 17;
  const midNight = 24;
  const afternoonGreeting = useTranslation('greetings.afternoon');
  const morningGreeting = useTranslation('greetings.morning');
  const eveningGreeting = useTranslation('greetings.evening');

  if (holidayCode) {
    const greeting = intl.formatMessage({ id: `greetings.${holidayCode}` });

    return [greeting, <></>];
  }

  if (currentHour >= afternoon && currentHour < evening) {
    return [afternoonGreeting, <Sun32 />];
  }

  if (currentHour >= evening && currentHour < midNight) {
    return [eveningGreeting, <Sunset32 />];
  }

  return [morningGreeting, <Sunrise32 />];
};

export const UserGreeting: FC = () => {
  const { user } = useUser();
  const [greeting, icon] = useGreeting();
  const name = fullName(user);
  const { isPreview, isPending } = useCustomerPreview();

  const message = (
    <>
      <strong>{greeting}</strong>
      {isPending || isPreview || isEmpty(name) ? null : `, ${name}!`}
    </>
  );

  return (
    <Page.Header.Title>
      <Page.Header.Title.Icon>{icon}</Page.Header.Title.Icon>
      <Page.Header.Title.Text>{message}</Page.Header.Title.Text>
    </Page.Header.Title>
  );
};
